import * as React from 'react';
import Link from 'next/link';
import { AppMenu, Flex, Text } from '@ghq-abi/design-system';

import { useNav } from '~/app/contexts/NavContext';
import { EEventsLabels } from '~/app/hocs/eventsAnalytics/eventsEnum';
import { useEvents } from '~/app/hocs/eventsAnalytics/useEvents';
import { RouteObject } from '~/app/types';
import { BackArrowIcon, OurAppsIcon } from '~/shared/components/icons';
import { HamburguerIcon } from '~/shared/components/icons/HamburgerIcon';

export function MobileMenu() {
    const {
        isAppMenuOpen,
        subMenuOpen,
        menuHeaderItem,
        menuItems,
        footerItems,
        toggleAppMenu,
        checkIfIsActive,
        toggleSubMenu,
    } = useNav();
    const { fireClickEvent } = useEvents();

    const onMenuItemClick = (item: RouteObject) => {
        if (item.routeComponents?.length) {
            toggleSubMenu(item);
        } else {
            toggleSubMenu(null);
            toggleAppMenu();
        }
    };

    const handleHelpSubItemClick = (path?: string) => {
        fireClickEvent({
            event: EEventsLabels.SOMETHING_NOT_WORKING_CLICK,
            page: 'navigation',
            component: 'MobileMenu',
            action: 'help_center_item_click',
            properties: {
                menuKey: 'isSomethingNotWorking',
            },
        });

        window.open(path, '_blank', 'noopener,noreferrer');
    };

    function getAsComponent(item: { external?: boolean; path?: string }) {
        if (item.external) return undefined;
        if (item.path) return Link;
        return undefined;
    }

    return (
        <AppMenu open={isAppMenuOpen}>
            <AppMenu.Trigger asChild>
                <AppMenu.MenuIconButton onClick={toggleAppMenu} icon={<HamburguerIcon />} />
            </AppMenu.Trigger>
            <AppMenu.Content
                overlayIsActive
                closeMenu={() => {
                    toggleAppMenu();
                    toggleSubMenu(null);
                }}
            >
                <AppMenu.Header css={{ p: 0 }}>
                    {subMenuOpen ? (
                        <Flex
                            align="center"
                            gap="sm"
                            css={{ width: '$full', height: '$appHeaderHeight', p: '$2' }}
                        >
                            <AppMenu.MenuIconButton
                                onClick={() => toggleSubMenu(null)}
                                icon={<BackArrowIcon />}
                            />
                            <Text css={{ fontWeight: '$medium' }}>{subMenuOpen.title || ''}</Text>
                        </Flex>
                    ) : (
                        <Flex
                            align="center"
                            justify="between"
                            css={{ width: '$full', height: '$appHeaderHeight', p: '$2' }}
                        >
                            <Text
                                css={{
                                    fontSize: '$2xl',
                                    fontWeight: '$bold',
                                    background:
                                        'linear-gradient(90deg, #D3A238 3.33%, #F5E003 40.91%)',
                                    backgroundClip: 'text',
                                    textFillColor: 'transparent',
                                    '-webkit-background-clip': 'text',
                                    '-webkit-text-fill-color': 'transparent',
                                }}
                            >
                                Boilerplate
                            </Text>
                            <AppMenu.MenuIconButton
                                onClick={() => toggleSubMenu(menuHeaderItem)}
                                icon={<OurAppsIcon />}
                            />
                        </Flex>
                    )}
                </AppMenu.Header>

                {subMenuOpen ? (
                    <AppMenu.MenuList>
                        {subMenuOpen?.routeComponents?.map((subitem) => (
                            <AppMenu.MenuItem
                                key={subitem.key}
                                as={Link}
                                title={subitem.title}
                                route={subitem.path}
                                leftIcon={subitem.icon}
                                onClick={() => onMenuItemClick(subitem)}
                                isSubMenuItem
                            />
                        ))}
                    </AppMenu.MenuList>
                ) : (
                    <>
                        <AppMenu.MenuList>
                            {menuItems.map((item) => (
                                <AppMenu.MenuItem
                                    key={item.key}
                                    as={getAsComponent(item)}
                                    route={item.external ? undefined : item.path}
                                    title={item.title}
                                    leftIcon={item.icon}
                                    hasSubmenu={!!item.routeComponents?.length}
                                    isActive={checkIfIsActive(
                                        item.path ?? '',
                                        item.routeComponents,
                                    )}
                                    onClick={() => {
                                        if (item.external) {
                                            window.open(item.path, '_blank', 'noopener,noreferrer');
                                        } else {
                                            onMenuItemClick(item);
                                        }
                                    }}
                                />
                            ))}
                        </AppMenu.MenuList>

                        <AppMenu.Footer>
                            {footerItems.map((item) => (
                                <AppMenu.MenuItem
                                    key={item.key}
                                    as={getAsComponent(item)}
                                    title={item.title}
                                    route={item.external ? undefined : item.path}
                                    hasSubmenu={!!item?.routeComponents?.length}
                                    leftIcon={item.icon}
                                    onClick={() => {
                                        if (item.key === 'help') {
                                            toggleSubMenu(item);
                                        } else {
                                            onMenuItemClick(item);
                                        }
                                    }}
                                    renderSubmenu={
                                        item.key === 'help' ? (
                                            <AppMenu.SubMenu
                                                title={item.title}
                                                renderTitle={
                                                    <Text
                                                        css={{
                                                            fontWeight: '$medium',
                                                            fontSize: '$sm',
                                                        }}
                                                    >
                                                        {item.title}
                                                    </Text>
                                                }
                                            >
                                                <AppMenu.MenuList>
                                                    {item.routeComponents?.map((subitem) => (
                                                        <AppMenu.MenuItem
                                                            key={subitem.key}
                                                            title={subitem.title}
                                                            route={
                                                                subitem.external
                                                                    ? undefined
                                                                    : subitem.path
                                                            }
                                                            isSubMenuItem
                                                            onClick={
                                                                subitem.external
                                                                    ? () =>
                                                                          handleHelpSubItemClick(
                                                                              subitem.path,
                                                                          )
                                                                    : () => onMenuItemClick(subitem)
                                                            }
                                                        />
                                                    ))}
                                                </AppMenu.MenuList>
                                            </AppMenu.SubMenu>
                                        ) : null
                                    }
                                />
                            ))}
                        </AppMenu.Footer>
                    </>
                )}
            </AppMenu.Content>
        </AppMenu>
    );
}
